// API client: tokens in localStorage, session id header, refresh on expiry.

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public code?: string,
    public data: Record<string, unknown> = {},
  ) {
    super(message);
  }
}

export interface Tokens {
  accessToken: string;
  idToken?: string;
  refreshToken: string;
  sessionId: string;
  device: "mobile" | "desktop";
  expiresAt: number;
  userId?: string;
}

const KEY = "aisol-med.tokens";

export function loadTokens(): Tokens | null {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as Tokens) : null;
  } catch {
    return null;
  }
}

export function saveTokens(t: Tokens | null): void {
  try {
    if (t) localStorage.setItem(KEY, JSON.stringify(t));
    else localStorage.removeItem(KEY);
  } catch {
    /* storage unavailable */
  }
  window.dispatchEvent(new CustomEvent("aisol:tokens"));
}

export function tokensFromResponse(r: { accessToken: string; idToken?: string; refreshToken: string; sessionId: string; device: "mobile" | "desktop"; expiresIn: number }): Tokens {
  const payload = JSON.parse(atob(r.accessToken.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
  return { accessToken: r.accessToken, idToken: r.idToken, refreshToken: r.refreshToken, sessionId: r.sessionId, device: r.device, expiresAt: Date.now() + (r.expiresIn - 60) * 1000, userId: payload.sub };
}

/** Phone or computer: decides the session length (30 days vs 12 h idle). Tablets count as phones. */
export function detectDevice(): "mobile" | "desktop" {
  const uaData = (navigator as Navigator & { userAgentData?: { mobile?: boolean } }).userAgentData;
  if (uaData?.mobile) return "mobile";
  const ua = navigator.userAgent;
  if (/Android|iPhone|iPad|iPod|Mobile|Silk/i.test(ua)) return "mobile";
  if (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1) return "mobile"; // iPadOS
  if (window.matchMedia?.("(pointer: coarse)").matches && !window.matchMedia("(pointer: fine)").matches) return "mobile";
  return "desktop";
}

let refreshing: Promise<Tokens | null> | null = null;

async function refresh(t: Tokens): Promise<Tokens | null> {
  if (!refreshing) {
    refreshing = (async () => {
      try {
        const r = await fetch("/api/auth/refresh", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ refreshToken: t.refreshToken, sessionId: t.sessionId }) });
        if (!r.ok) {
          if (r.status === 401 || r.status === 403) {
            saveTokens(null);
            window.dispatchEvent(new CustomEvent("aisol:session-expired"));
          }
          return null;
        }
        const next = tokensFromResponse(await r.json());
        saveTokens(next);
        return next;
      } catch {
        return null; // offline: keep tokens
      } finally {
        setTimeout(() => (refreshing = null), 0);
      }
    })();
  }
  return refreshing;
}

export async function authHeaders(): Promise<Record<string, string>> {
  let t = loadTokens();
  if (!t) throw new ApiError(401, "Войдите снова", "UNAUTHORIZED");
  if (Date.now() > t.expiresAt) t = (await refresh(t)) ?? t;
  return { authorization: `Bearer ${t.accessToken}`, "x-session-id": t.sessionId };
}

export interface RequestOptions {
  method?: string;
  body?: unknown;
  raw?: BodyInit;
  contentType?: string;
  query?: Record<string, string | number | boolean | undefined | null>;
  signal?: AbortSignal;
  public?: boolean;
}

export async function api<T = unknown>(path: string, opts: RequestOptions = {}): Promise<T> {
  const qs = opts.query
    ? "?" +
      Object.entries(opts.query)
        .filter(([, v]) => v !== undefined && v !== null && v !== "")
        .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
        .join("&")
    : "";
  const url = `/api${path}${qs === "?" ? "" : qs}`;
  for (let attempt = 0; attempt < 2; attempt++) {
    const headers: Record<string, string> = opts.public ? {} : await authHeaders();
    let body: BodyInit | undefined;
    if (opts.raw !== undefined) {
      body = opts.raw;
      headers["content-type"] = opts.contentType ?? "application/octet-stream";
    } else if (opts.body !== undefined) {
      body = JSON.stringify(opts.body);
      headers["content-type"] = "application/json";
    }
    let r: Response;
    try {
      r = await fetch(url, { method: opts.method ?? (body ? "POST" : "GET"), headers, body, signal: opts.signal });
    } catch (e) {
      if ((e as Error).name === "AbortError") throw e;
      throw new ApiError(0, "Нет связи с сервером. Проверьте интернет", "OFFLINE");
    }
    if (r.status === 401 && !opts.public && attempt === 0) {
      const t = loadTokens();
      if (t && (await refresh(t))) continue;
    }
    const text = await r.text();
    let data: Record<string, unknown> = {};
    try {
      data = text ? JSON.parse(text) : {};
    } catch {
      data = { error: text };
    }
    if (!r.ok) {
      if (r.status === 401 && !opts.public) window.dispatchEvent(new CustomEvent("aisol:session-expired"));
      throw new ApiError(r.status, String(data.error ?? `Ошибка ${r.status}`), data.code as string | undefined, data);
    }
    return data as T;
  }
  throw new ApiError(401, "Войдите снова", "UNAUTHORIZED");
}

export const isOffline = (e: unknown) => e instanceof ApiError && e.code === "OFFLINE";
